import React, { useMemo } from 'react'

import { useTheme } from '@emotion/react'
import { useTranslation } from 'react-i18next'
import { SingleValue } from 'react-select'
import Async from 'react-select/async'

import { TypeaheadDropdown } from '@cash-design-system/react'
import { useArcadeMigrationFeatureFlag } from '@cash-web/shared-data-access-arcade-migration'

import { createLegacyMenuComponent, useMenuCloseControl } from '../Dropdown/LegacyReactSelectMenu'
import { FieldController } from '../FieldController/FieldController'
import { Option } from './useAutocompleteAddressInput'

type AutocompleteAddressProps = {
  chooseAddress: (option: SingleValue<Option>) => Promise<void>
  updateAutocompleteValue: (value: string) => Promise<Option[]>
}

export const AutocompleteAddress = ({ chooseAddress, updateAutocompleteValue }: AutocompleteAddressProps) => {
  const theme = useTheme()
  const { t } = useTranslation()
  const isArcadeMigrationEnabled = useArcadeMigrationFeatureFlag('apps')
  const [items, setItems] = React.useState<Option[]>([])
  const [query, setQuery] = React.useState('')

  const { isMenuOpen, setIsMenuOpen, controlRef } = useMenuCloseControl()
  const MenuComponent = useMemo(() => createLegacyMenuComponent<Option>(controlRef, { flipMenu: false }), [controlRef])

  React.useEffect(() => {
    if (!query) {
      setItems([])
      return
    }

    let cancelled = false
    updateAutocompleteValue(query).then(options => {
      if (!cancelled) {
        setItems(options)
      }
    })

    return () => {
      cancelled = true
    }
  }, [query, updateAutocompleteValue])

  const handleInputChange = React.useCallback((newQuery: string) => {
    setQuery(newQuery)
  }, [])

  if (isArcadeMigrationEnabled) {
    return (
      <FieldController
        FieldComponent={TypeaheadDropdown}
        componentProps={{
          inputAriaLabel: t('Enter your address', { context: 'Label on input' }),
          clearIconAriaLabel: t('Clear address', { context: 'Clear button aria label' }),
          expandIconAriaLabel: t('Expand address suggestions', { context: 'Expand button aria label' }),
          placeholder: t('Enter your address', { context: 'Placeholder for address input' }),
          getItemKey: (item: unknown) => (item as Option).placeId,
          itemToString: (item: unknown) => (item as Option).label,
          maxDropdownHeightPx: 336,
          onItemSelected: (item: unknown) => {
            // Only call chooseAddress when a full option is selected from the dropdown
            if (item && typeof item !== 'string') {
              chooseAddress(item as Option)
            }
          },
          onInputChange: handleInputChange,
          items: items,
          testId: 'autocomplete-address',
        }}
        name="autocompleteAddress"
      />
    )
  }

  return (
    <div ref={controlRef}>
      <Async
        loadOptions={updateAutocompleteValue}
        onChange={chooseAddress}
        placeholder="Enter your address"
        noOptionsMessage={() => null}
        autoFocus={true}
        aria-label={t('Enter your address', { context: 'Label on input' })}
        components={{
          Menu: MenuComponent,
        }}
        onMenuOpen={() => setIsMenuOpen(true)}
        onMenuClose={() => setIsMenuOpen(false)}
        menuIsOpen={isMenuOpen}
        styles={{
          container: base => {
            return {
              ...base,
              fontSize: '1.125rem',
              lineHeight: '1.375rem',
              fontWeight: theme.weights.regular,
              caretColor: theme.colors.primary,
              color: theme.colors.body,
              cursor: 'text',
            }
          },
          control: (base, props) => {
            const { isFocused } = props

            let stateSpecificStyles = {}
            if (isFocused) {
              stateSpecificStyles = {
                ...stateSpecificStyles,
                backgroundColor: theme.colors.background,
                boxShadow: `0 0 0 0.125rem ${theme.colors.primary} inset`,
              }
            } else {
              stateSpecificStyles = {
                ...stateSpecificStyles,
                backgroundColor: theme.colors.formFieldBackground,
                boxShadow: `0 0 0 0.125rem ${theme.colors.formFieldBackground} inset`,
                cursor: 'text',
                overflow: 'hidden',
              }
            }

            return {
              ...base,
              borderRadius: '1rem',
              border: 'none',
              cursor: 'text',
              outline: 'none',
              transition: 'none',
              ...stateSpecificStyles,
            }
          },
          indicatorsContainer: () => {
            return { display: 'none' }
          },
          input: base => {
            return {
              ...base,
              color: theme.colors.label,
              marginLeft: '0.375rem',
            }
          },
          menu: base => {
            return {
              ...base,
              backgroundColor: theme.colors.background,
              borderRadius: '0.75rem',
              border: `0.0625rem solid ${theme.colors.outline}`,
              overflow: 'hidden',
              boxShadow: '0px 0.75rem 1.5rem rgba(0, 0, 0, 0.04)',
            }
          },
          menuList: (base, props) => {
            return {
              ...base,
              color: theme.colors.label,
              maxHeight: '21rem',
              padding: 'unset',
            }
          },
          option: (base, props) => {
            const { isFocused } = props

            let stateSpecificStyles = {}
            if (isFocused) {
              stateSpecificStyles = {
                backgroundColor: theme.colors.clickableContentBackgroundHover,
              }
            }

            return {
              ...base,
              borderBottom: `0.0625rem solid ${theme.colors.outline}`,
              cursor: 'pointer',
              padding: `${theme.spacing.s} ${theme.spacing.m}`,
              ...stateSpecificStyles,

              '&:last-child': {
                borderBottom: 'none',
              },
            }
          },
          placeholder: base => {
            return {
              ...base,
              lineHeight: '1.5rem',
              fontSize: '1.125rem',
              color: theme.colors.placeholderLabel,
              marginLeft: '0.375rem',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }
          },
          valueContainer: base => {
            return {
              ...base,
              minHeight: '4rem',
              padding: theme.spacing.s,
              rowGap: '0.5rem',
              columnGap: '0.5rem',
            }
          },
        }}
      />
    </div>
  )
}
